import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, ArrowRight, ShieldCheck, Sparkles, Calculator, Gift, Building2 } from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const PLAN_META = {
    free: {
        name: "Découverte",
        price: 0,
        badge: { label: "Gratuit", icon: Gift, className: "bg-[var(--tk-input-bg)] text-[var(--tk-text)] border border-[var(--tk-border)]" },
        tagline: "Pour découvrir la puissance de Ticketify",
        cta: "Commencer gratuitement",
        features: [
            "Relevés bancaires illimités",
            "Jusqu'à 100 transactions justifiées",
            "Connexion Gmail",
            "Recherche automatique des justificatifs",
            "Export PDF",
            "Historique 30 jours",
        ],
    },
    pro: {
        name: "Pro",
        price: 39.99,
        badge: { label: "Le plus populaire", icon: Sparkles, className: "bg-[#007AFF] text-white shadow-[0_4px_14px_rgba(0,122,255,0.35)]" },
        tagline: "Pour les indépendants et TPE qui veulent gagner du temps",
        cta: "Passer au Pro",
        featured: true,
        features: [
            "Relevés bancaires illimités",
            "Jusqu'à 1 000 transactions justifiées / mois",
            "Connexion Gmail",
            "Association automatique des justificatifs",
            "Export CSV",
            "Export PDF",
            "Historique illimité",
            "Support prioritaire",
        ],
    },
    business: {
        name: "Business",
        price: 149.99,
        badge: { label: "Entreprise", icon: Building2, className: "bg-[#34C759] text-white shadow-[0_4px_14px_rgba(52,199,89,0.3)]" },
        tagline: "Pour les équipes et cabinets à fort volume",
        cta: "Contacter l'équipe",
        features: [
            "Transactions illimitées",
            "Relevés illimités",
            "Multi-utilisateurs",
            "API",
            "Export CSV",
            "Export PDF",
            "Historique illimité",
            "Support dédié",
        ],
    },
};

const PLAN_ORDER = ["free", "pro", "business"];

const COMPARE_ROWS = [
    { label: "Relevés bancaires importés", values: ["Illimité", "Illimité", "Illimité"] },
    { label: "Transactions justifiées / mois", values: ["100", "1 000", "Illimité"] },
    { label: "Connexion Gmail", values: ["check", "check", "check"] },
    { label: "Recherche automatique des justificatifs", values: ["check", "check", "check"] },
    { label: "Association automatique aux transactions", values: ["x", "check", "check"] },
    { label: "Export PDF", values: ["check", "check", "check"] },
    { label: "Export CSV", values: ["x", "check", "check"] },
    { label: "Historique", values: ["30 jours", "Illimité", "Illimité"] },
    { label: "Support prioritaire", values: ["x", "check", "check"] },
    { label: "Multi-utilisateurs", values: ["x", "x", "check"] },
    { label: "API", values: ["x", "x", "check"] },
    { label: "Support dédié", values: ["x", "x", "check"] },
];


export default function Tarifs() {
    const { user } = useAuth();
    const ctaTarget = user ? "/app" : "/register";
    const [apiPlans, setApiPlans] = useState([]);
    // Billing cycle: "monthly" | "quarterly" (3 mois) | "yearly"
    const [billingCycle, setBillingCycle] = useState("monthly");
    const annual = billingCycle === "yearly";
    const [roi, setRoi] = useState({ tickets: 60 });

    useEffect(() => {
        let cancelled = false;
        api.get("/payments/plans")
            .then((r) => {
                if (!cancelled) setApiPlans(r.data);
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    // Merge live prices from the API (source of truth for billing) into the premium meta.
    const display = PLAN_ORDER.map((id) => {
        const meta = PLAN_META[id];
        const live = apiPlans.find((p) => p.id === id);
        return { id, ...meta, price: live?.price ?? meta.price };
    });

    const priceFor = (p) => {
        if (p.price === 0) return { monthly: 0, billed: 0, save: 0, suffix: null };
        if (billingCycle === "monthly") {
            return { monthly: p.price, billed: p.price, save: 0, suffix: null };
        }
        if (billingCycle === "quarterly") {
            const monthly = p.price * 0.9; // -10 %
            return {
                monthly,
                billed: monthly * 3,
                save: (p.price - monthly) * 3,
                suffix: "Facturé tous les 3 mois",
            };
        }
        // yearly — -20 %
        const monthly = p.price * 0.8;
        return {
            monthly,
            billed: monthly * 12,
            save: (p.price - monthly) * 12,
            suffix: "Facturé annuellement",
        };
    };

    const roiCompute = useMemo(() => {
        const tickets = Math.max(0, Math.min(2000, Number(roi.tickets) || 0));
        // ~25% des justificatifs papier sont actuellement perdus / non récupérés en TVA
        const lostRate = 0.25;
        const vatPerTicket = 1.8; // 20% sur justificatif moyen 9€
        const yearlyVat = +(tickets * 12 * lostRate * vatPerTicket).toFixed(0);
        const totalArchived = tickets * 12 * 10;
        const findRate = 99;
        const proPrice = PLAN_META.pro.price;
        const planCost = annual ? proPrice * 0.8 * 12 : proPrice * 12;
        const totalGain = +(yearlyVat - planCost).toFixed(0);
        return {
            yearlyVat,
            totalArchived,
            findRate,
            planCost: +planCost.toFixed(0),
            totalGain,
        };
    }, [roi.tickets, annual]);

    return (
        <div className="min-h-screen">
            <PublicHeader />

            {/* Hero */}
            <section className="pt-32 pb-12 sm:pt-44 sm:pb-16 px-6 sm:px-8 text-center">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#86868B] mb-3 tk-rise">Tarifs</p>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#1D1D1F] tk-rise tk-rise-1 leading-[1.05]">
                    Sans engagement.
                </h1>
                <p className="mt-5 text-lg text-[#86868B] tk-rise tk-rise-2 max-w-2xl mx-auto">
                    Tous les plans utilisent notre IA pour retrouver automatiquement les justificatifs
                    associés à vos transactions bancaires.
                </p>

                {/* Billing cycle toggle */}
                <div className="mt-8 inline-flex items-center gap-1 p-1 rounded-full bg-[#F5F5F7] border border-black/5">
                    <button
                        onClick={() => setBillingCycle("monthly")}
                        data-testid="period-monthly"
                        className={`px-5 py-2 rounded-full text-sm transition ${
                            billingCycle === "monthly" ? "bg-white text-[#1D1D1F] shadow-sm" : "text-[#86868B]"
                        }`}
                    >
                        Mensuel
                    </button>
                    <button
                        onClick={() => setBillingCycle("quarterly")}
                        data-testid="period-quarterly"
                        className={`px-5 py-2 rounded-full text-sm transition flex items-center gap-2 ${
                            billingCycle === "quarterly" ? "bg-white text-[#1D1D1F] shadow-sm" : "text-[#86868B]"
                        }`}
                    >
                        3 mois
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#34C759]/15 text-[#34C759]">
                            -10 %
                        </span>
                    </button>
                    <button
                        onClick={() => setBillingCycle("yearly")}
                        data-testid="period-annual"
                        className={`px-5 py-2 rounded-full text-sm transition flex items-center gap-2 ${
                            billingCycle === "yearly" ? "bg-white text-[#1D1D1F] shadow-sm" : "text-[#86868B]"
                        }`}
                    >
                        Annuel
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#34C759]/15 text-[#34C759]">
                            -20 %
                        </span>
                    </button>
                </div>
            </section>

            {/* Pricing grid */}
            <section className="px-6 sm:px-8 pb-6 sm:pb-8">
                <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-6 items-stretch md:items-center">
                    {display.map((p, i) => {
                        const featured = !!p.featured;
                        const pricing = priceFor(p);
                        const BadgeIcon = p.badge.icon;
                        return (
                            <div
                                key={p.id}
                                data-testid={`tarifs-card-${p.id}`}
                                className={`tk-card relative flex flex-col p-8 pt-10 rounded-[28px] tk-rise ${
                                    featured
                                        ? "border-2 border-[#007AFF] shadow-[0_20px_50px_rgba(0,122,255,0.15)] md:py-11 md:-my-4 md:scale-[1.02] z-10"
                                        : "hover:shadow-[0_10px_30px_rgba(0,0,0,0.08)] hover:-translate-y-0.5"
                                }`}
                                style={{ animationDelay: `${i * 100}ms` }}
                            >
                                <span
                                    className={`absolute -top-3.5 left-8 inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full ${p.badge.className}`}
                                >
                                    <BadgeIcon className="w-3 h-3" />
                                    {p.badge.label}
                                </span>

                                <h3 className="text-lg font-semibold tracking-tight text-[#1D1D1F]">{p.name}</h3>
                                <p className="mt-0.5 text-xs text-[#86868B]">{p.tagline}</p>

                                <div className="mt-6 flex items-baseline gap-1">
                                    <span className={`font-semibold tracking-tight text-[#1D1D1F] ${featured ? "text-5xl" : "text-4xl"}`}>
                                        {pricing.monthly === 0
                                            ? "0€"
                                            : `${pricing.monthly.toFixed(2).replace(".", ",")}€`}
                                    </span>
                                    <span className="text-sm text-[#86868B]">/ mois</span>
                                </div>
                                {billingCycle === "yearly" && pricing.save > 0 && (
                                    <p className="mt-1.5 text-xs text-[#34C759] font-semibold">
                                        Économisez {pricing.save.toFixed(0)}€ / an · {pricing.suffix}
                                    </p>
                                )}
                                {billingCycle === "quarterly" && pricing.save > 0 && (
                                    <p className="mt-1.5 text-xs text-[#34C759] font-semibold">
                                        Économisez {pricing.save.toFixed(0)}€ / trim. · {pricing.suffix}
                                    </p>
                                )}
                                {billingCycle === "monthly" && pricing.monthly > 0 && (
                                    <p className="mt-1.5 text-xs text-[#86868B]">Facturé mensuellement</p>
                                )}

                                <div className="mt-6 h-px bg-[var(--tk-border)]" />

                                <ul className="mt-6 space-y-3 mb-8 flex-1">
                                    {p.features.map((f) => (
                                        <li key={f} className="flex items-start gap-2.5 text-sm text-[#1D1D1F]">
                                            <span
                                                className={`mt-0.5 flex-shrink-0 rounded-full flex items-center justify-center ${
                                                    featured ? "bg-[rgba(0,122,255,0.12)]" : "bg-[rgba(52,199,89,0.12)]"
                                                }`}
                                                style={{ width: 18, height: 18 }}
                                            >
                                                <Check className={`w-3 h-3 ${featured ? "text-[#007AFF]" : "text-[#34C759]"}`} strokeWidth={3} />
                                            </span>
                                            <span>{f}</span>
                                        </li>
                                    ))}
                                </ul>
                                <Link
                                    to={ctaTarget}
                                    data-testid={`tarifs-cta-${p.id}`}
                                    className={`tk-btn w-full justify-center ${
                                        featured
                                            ? "tk-btn-blue shadow-[0_8px_20px_rgba(0,122,255,0.35)] hover:shadow-[0_10px_26px_rgba(0,122,255,0.45)]"
                                            : "tk-btn-secondary hover:!opacity-100 hover:bg-[#1D1D1F] hover:text-white"
                                    }`}
                                >
                                    {user ? "Accéder à mon compte" : p.cta}
                                </Link>
                                {p.id !== "free" && (
                                    <p className="mt-2.5 text-[11px] text-[#86868B] text-center">
                                        Sans engagement · Annulable en 1 clic
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>
                <p className="mt-10 text-xs text-[#86868B] text-center" data-testid="tarifs-footer-note">
                    ✓ Sans engagement&nbsp;&nbsp;•&nbsp;&nbsp;Paiement sécurisé par Stripe&nbsp;&nbsp;•&nbsp;&nbsp;Changement de forfait à tout moment
                </p>
            </section>

            {/* Trust strip */}
            <section className="px-6 sm:px-8 py-16">
                <div className="max-w-3xl mx-auto flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-[#86868B]">
                    <span className="inline-flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-[#34C759]" /> Paiement sécurisé Stripe
                    </span>
                    <span className="inline-flex items-center gap-2">
                        <Check className="w-4 h-4 text-[#34C759]" />
                        {billingCycle === "monthly" ? "Annulation en 1 clic" : "Annulez le renouvellement à tout moment"}
                    </span>
                    <span className="inline-flex items-center gap-2">
                        <Check className="w-4 h-4 text-[#34C759]" /> RGPD · données en France
                    </span>
                </div>
            </section>

            {/* COMPARISON TABLE */}
            <section className="px-6 sm:px-8 pb-20" data-testid="comparison-table">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-10">
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#86868B] mb-3">
                            Comparaison détaillée
                        </p>
                        <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1D1D1F]">
                            Trouvez le plan qui vous correspond.
                        </h2>
                    </div>
                    <div className="tk-card overflow-hidden">
                        <div className="grid grid-cols-[1.6fr_repeat(3,1fr)] sm:grid-cols-[2fr_repeat(3,1fr)] text-sm">
                            {/* Header row */}
                            <div className="p-4 text-xs font-bold uppercase tracking-wider text-[#86868B] border-b border-black/5">
                                Fonctionnalité
                            </div>
                            {["Découverte", "Pro", "Business"].map((p) => (
                                <div
                                    key={p}
                                    className={`p-4 text-center text-xs font-bold uppercase tracking-wider border-b border-black/5 ${p === "Pro" ? "text-[#007AFF] bg-[#007AFF]/[0.04]" : "text-[#1D1D1F]"}`}
                                >
                                    {p}
                                </div>
                            ))}
                            {/* Body rows */}
                            {COMPARE_ROWS.map((row) => (
                                <RowFragment key={row.label} row={row} />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ROI CALCULATOR */}
            <section className="px-6 sm:px-8 pb-24" data-testid="roi-calculator">
                <div className="max-w-4xl mx-auto tk-card p-8 sm:p-10">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#007AFF] to-[#34C759] flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-white" />
                        </div>
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#86868B]">
                            Combien Ticketify vous rapporte
                        </p>
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#1D1D1F]">
                        Votre gain estimé.
                    </h2>
                    <p className="mt-2 text-sm text-[#86868B]">
                        Basé sur ~25 % de justificatifs papier actuellement non récupérés (TVA perdue) et un justificatif moyen à 9 € TTC (TVA 20 %).
                    </p>

                    <div className="mt-7">
                        <label className="block text-xs font-bold uppercase tracking-wider text-[#86868B] mb-2">
                            Justificatifs par mois&nbsp;: <span className="text-[#1D1D1F] font-mono">{roi.tickets}</span>
                        </label>
                        <input
                            type="range"
                            min={5}
                            max={500}
                            step={5}
                            value={roi.tickets}
                            onChange={(e) => setRoi({ tickets: Number(e.target.value) })}
                            data-testid="roi-slider"
                            className="w-full accent-[#007AFF]"
                        />
                        <div className="flex justify-between text-[10px] text-[#86868B] mt-1 font-mono">
                            <span>5</span>
                            <span>50</span>
                            <span>100</span>
                            <span>250</span>
                            <span>500</span>
                        </div>
                    </div>

                    <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <RoiKpi label="TVA récupérée / an" value={`${roiCompute.yearlyVat.toLocaleString("fr-FR")} €`} accent="green" />
                        <RoiKpi label="Justificatifs archivés sur 10 ans" value={roiCompute.totalArchived.toLocaleString("fr-FR")} />
                        <RoiKpi label="Retrouvés en 1 recherche" value={`${roiCompute.findRate}%`} accent="blue" />
                        <RoiKpi label="Conservation légale" value="10 ans" />
                    </div>

                    <div className="mt-8 p-6 rounded-2xl bg-gradient-to-br from-[#007AFF]/[0.08] to-[#34C759]/[0.08] border border-[#007AFF]/15">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                            <div>
                                <p className="text-xs font-bold uppercase tracking-wider text-[#86868B]">
                                    Gain net annuel (après coût du plan Pro)
                                </p>
                                <p className="mt-1 text-4xl sm:text-5xl font-semibold tracking-tight text-[#1D1D1F]">
                                    + {roiCompute.totalGain.toLocaleString("fr-FR")} €
                                </p>
                            </div>
                            <Link
                                to={ctaTarget}
                                className="tk-btn tk-btn-primary tk-cta-glow"
                                data-testid="roi-cta"
                            >
                                {user ? "Aller au tableau de bord" : "Récupérer cet argent"} <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* PROOF — honest, non-fabricated */}
            <section className="px-6 sm:px-8 pb-24" data-testid="trust-strip">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#86868B] mb-3">
                            Pourquoi nous faire confiance
                        </p>
                        <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1D1D1F]">
                            Les chiffres parlent d&apos;eux-mêmes.
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-5">
                        {[
                            {
                                kpi: "10 ans",
                                title: "Conservation légale",
                                body: "Vos justificatifs sont archivés conformément à l'art. L102 B LPF — durée légale française pour pièces comptables.",
                            },
                            {
                                kpi: "< 3 sec",
                                title: "Extraction OCR",
                                body: "GPT-5.2 Vision identifie marchand, date, montant et TVA en moins de 3 secondes par justificatif photographié.",
                            },
                            {
                                kpi: "RGPD",
                                title: "Données hébergées en UE",
                                body: "Serveurs européens, chiffrement TLS partout, droit à l'effacement en 1 clic, aucune revente de données.",
                            },
                        ].map((it, i) => (
                            <div
                                key={it.title}
                                className="tk-card p-6 tk-rise"
                                style={{ animationDelay: `${i * 100}ms` }}
                                data-testid={`trust-item-${i}`}
                            >
                                <p
                                    className="text-4xl font-semibold tracking-tight text-[#1D1D1F] mb-3"
                                >
                                    {it.kpi}
                                </p>
                                <p className="text-sm font-semibold text-[#1D1D1F] mb-2">{it.title}</p>
                                <p className="text-[14px] text-[#1D1D1F]/85 leading-relaxed">{it.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ short */}
            <section className="px-6 sm:px-8 pb-24">
                <div className="max-w-3xl mx-auto">
                    <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#1D1D1F] mb-8">
                        Questions fréquentes
                    </h2>
                    <div className="tk-card divide-y divide-black/5 overflow-hidden">
                        {[
                            ["Le plan Découverte est-il vraiment gratuit ?", "Oui. Relevés bancaires et analyse illimités, jusqu'à 100 transactions justifiées, sans carte demandée. Vous pouvez passer en Pro à tout moment si vous avez besoin de plus."],
                            ["Puis-je changer de plan ?", "À tout moment. Le passage est immédiat, le prorata calculé."],
                            ["Y a-t-il un engagement ?", "Aucun. Tout est sans engagement, annulable en 1 clic."],
                            ["Mes données sont-elles en sécurité ?", "Hébergement France, chiffrement, conformes RGPD et conservation comptable (L102 B LPF)."],
                        ].map(([q, a]) => (
                            <div key={q} className="p-6">
                                <p className="font-semibold text-[#1D1D1F]">{q}</p>
                                <p className="mt-2 text-sm text-[#86868B] leading-relaxed">{a}</p>
                            </div>
                        ))}
                    </div>
                    <p className="mt-6 text-center text-sm">
                        <Link to="/faq" className="text-[#007AFF] font-semibold hover:underline">
                            Voir toutes les questions →
                        </Link>
                    </p>
                </div>
            </section>

            {/* Final CTA */}
            <section className="px-6 sm:px-8 pb-24">
                <div className="max-w-3xl mx-auto text-center">
                    <Sparkles className="w-9 h-9 text-[#86868B] mx-auto mb-4" />
                    <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1D1D1F]">
                        Démarrez gratuitement.
                    </h2>
                    <p className="mt-3 text-[#86868B]">Jusqu'à 100 transactions justifiées gratuitement. Sans carte, sans engagement.</p>
                    <Link
                        to={ctaTarget}
                        className="tk-btn tk-btn-primary mt-6 inline-flex tk-cta-glow"
                        data-testid="tarifs-cta-bottom"
                    >
                        {user ? "Aller au tableau de bord" : "Créer mon compte"} <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>

            <footer className="border-t border-black/5 py-10 px-6 sm:px-8 pb-28 sm:pb-32 text-center text-sm text-[#86868B]">
                © 2026 Ticketify
            </footer>
        </div>
    );
}


function Cell({ kind }) {
    if (kind === "check") return <Check className="w-4 h-4 text-[#34C759] mx-auto" />;
    if (kind === "x") return <X className="w-4 h-4 text-[#86868B]/50 mx-auto" />;
    return <span className="text-sm font-medium text-[#1D1D1F]">{kind}</span>;
}

function RowFragment({ row }) {
    const COLS = ["free", "pro", "business"];
    return (
        <>
            <div className="p-3.5 text-sm text-[#1D1D1F] border-b border-black/5">{row.label}</div>
            {row.values.map((v, i) => (
                <div
                    key={`${row.label}-${COLS[i]}`}
                    className={`p-3.5 text-center text-sm border-b border-black/5 ${i === 1 ? "bg-[#007AFF]/[0.04]" : ""}`}
                >
                    <Cell kind={v} />
                </div>
            ))}
        </>
    );
}

function RoiKpi({ label, value, accent }) {
    const tone =
        accent === "blue" ? "text-[#007AFF]" : accent === "green" ? "text-[#34C759]" : "text-[#1D1D1F]";
    return (
        <div className="rounded-2xl bg-[#F5F5F7] border border-black/5 p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#86868B]">{label}</p>
            <p className={`mt-1 text-xl sm:text-2xl font-semibold ${tone} tabular-nums`}>{value}</p>
        </div>
    );
}
